import moment from "moment";
import React from "react";
import _ from "lodash";
import { filter, map, size, uniq, find } from "lodash";
import ReactLoading from "react-loading";
import readingSubTypes from "store/reducers/readingSubType.json";
import { withIcon } from "../Violations/NewViolations/violationHelpers";

import { Col, Row } from "reactstrap";

function percentage(value, totalValue) {
  return ((value / totalValue) * 100).toFixed(2);
}

function formatViolationNotifications(notifications) {
  return notifications && notifications.length > 0 ? notifications : "none";
}

const ATTESTATION_RULE_TYPES = [
  "Vitals",
  "Weight",
  "Trending Violation",
  "Event Expectation",
];

function normalizeSubTypeName(name) {
  return name.replace("Pulse", "Heart Rate");
}

function isEventViolation(violation) {
  if (!violation) return false;
  if (
    violation.ruleType === "Trending Violation" ||
    violation.ruleType === "Event Expectation"
  ) {
    return true;
  }
  return violation.name && violation.name.toLowerCase().includes("event");
}

function getViolationCategory(violation) {
  if (violation.ruleType === "Event Expectation") {
    if (violation.name && violation.name.includes("(Back in range)")) {
      return "Back in range";
    }
    if (violation.name && violation.name.includes("(Event retest not confirmed)")) {
      return "Retest not confirmed";
    }
    if (violation.name && violation.name.includes("(Event retest confirmed)")) {
      return "Retest confirmed";
    }
    return "Event";
  }
  if (violation.ruleType === "Trending Violation") {
    return "Trending Event";
  }
  if (violation.name && violation.name.toLowerCase().includes("event")) {
    return "Event";
  }
  return "Exception";
}

function ViolationTypeCell({ violation }) {
  const ruleType = violation.ruleType || "Vitals";
  const category = getViolationCategory(violation);

  return (
    <div className="violation-type-cell text-center">
      <div className="d-flex align-items-center justify-content-center flex-wrap">
        {withIcon(ruleType)}
      </div>
    </div>
  );
}

function violationMatchesReading(o, opts) {
  const {
    subType,
    description,
    reading,
    isBodytrace,
    readingsSource,
    bodytraceReadings,
  } = opts;

  if (!ATTESTATION_RULE_TYPES.includes(o.ruleType)) {
    return false;
  }

  const normalizedSubType = normalizeSubTypeName(subType);
  const readingsList = isBodytrace ? bodytraceReadings : readingsSource;
  const hasReadingsInSection = readingsList && readingsList.length > 0;

  if (
    !o.reading &&
    hasReadingsInSection &&
    (o.ruleType === "Trending Violation" || o.ruleType === "Event Expectation")
  ) {
    const violationSubType = normalizeSubTypeName(o.readingSubType || "");
    if (!violationSubType) {
      return false;
    }
    if (isBodytrace) {
      if (subType === "Weight") {
        return false;
      }
      return violationSubType === normalizedSubType;
    }
    if (description === "Weight Difference") {
      return false;
    }
    if (subType === "Heart Rate") {
      return violationSubType === subType;
    }
    return violationSubType === subType;
  }

  const checkReading = _.find(readingsList, {
    _id: o.reading ? o.reading._id : "",
  });

  if (!checkReading) {
    return false;
  }

  if (o.ruleType === "Weight") {
    if (description === "Weight Difference" || subType === "Weight") {
      if (isBodytrace) {
        return o.isBodytraceReading && o.reading;
      }
      return (
        !o.isBodytraceReading && o.reading && o.reading.readingType === reading
      );
    }
    return false;
  }

  if (isBodytrace) {
    if (subType === "Weight") {
      return false;
    }
    return (
      o.isBodytraceReading &&
      o.readingSubType === normalizedSubType &&
      o.reading
    );
  }

  if (subType === "Heart Rate") {
    return (
      !o.isBodytraceReading &&
      o.readingSubType === subType &&
      o.reading &&
      o.reading.readingType === reading
    );
  }

  if (description === "Weight Difference") {
    return false;
  }

  return !o.isBodytraceReading && o.readingSubType === subType && o.reading;
}

function formatViolationCondition(violation) {
  const rule = violation.ruleExpectation;
  if (!rule || (Array.isArray(rule) && rule.length === 0)) {
    return "-";
  }

  if (violation.ruleType === "Trending Violation") {
    if (rule.numberOfTimes && rule.repeatPer) {
      return `${rule.numberOfTimes} times within ${rule.repeatPer}`;
    }
  }

  if (rule.condition) {
    if (
      rule.condition === "between" &&
      rule.valueProperty &&
      rule.valueProperty[0]
    ) {
      return `${rule.condition} - ${rule.valueProperty[0].minValue} TO ${rule.valueProperty[0].maxValue}`;
    }
    if (rule.valueProperty && rule.valueProperty[0]) {
      return `${rule.condition} ${rule.valueProperty[0].value || ""}`.trim();
    }
    if (rule.gainLossPerDay) {
      return `Pound loss/gain per Day - ${rule.gainLossPerDay}`;
    }
    return rule.condition;
  }

  return "-";
}

function getViolationNotificationDisplay(violation) {
  const isEventExpectation = violation.ruleType === "Event Expectation";
  const isBackInRange = violation.name && violation.name.includes("(Back in range)");
  const isEventRetestNotConfirmed =
    violation.name && violation.name.includes("Event retest not confirmed");

  const isEventRetestConfirmed =
    violation.name && violation.name.includes("Event retest confirmed");

  if (isEventExpectation && isBackInRange) {
    return { patient: "none", provider: "none", staff: "none" };
  }

  if (isEventExpectation && isEventRetestNotConfirmed) {
    return {
      patient: "none",
      provider: formatViolationNotifications(violation.providerNotifications),
      staff: formatViolationNotifications(violation.staffNotifications),
    };
  }

  if (isEventExpectation && isEventRetestConfirmed) {
    return {
      patient: formatViolationNotifications(violation.patientNotifications),
      provider: formatViolationNotifications(violation.providerNotifications),
      staff: formatViolationNotifications(violation.staffNotifications),
    };
  }

  if (isEventExpectation) {
    return {
      patient: formatViolationNotifications(violation.patientNotifications),
      provider: "none",
      staff: "none",
    };
  }

  return {
    patient: formatViolationNotifications(violation.patientNotifications),
    provider: formatViolationNotifications(violation.providerNotifications),
    staff: formatViolationNotifications(violation.staffNotifications),
  };
}
function ReadingSubTypeSummary(props) {
  const profileOwner = props.profileOwner;
  const profile = props.profile;

  if (!profileOwner && !profile) {
    return (
      <ReactLoading type="spin" className="text-center mx-auto" color="blue" />
    );
  }

  if (size(profile.devices) == 0) {
    return (
      <div className="col-12" style={{padding:15}}>
        <h3 className="text-center">No Current Device Assigned</h3>
      </div>
    );
  }
  var readingSubType = readingSubTypes.filter((item) => item.description !== "Weight");
 
  return readingSubType.map((value, i) => {
    // this added code for heart rate take from puls ox filter(HeartRateFromPulsOx)
    if (props.isBloodPressureReading && value && value._id == '5be56cbe3863cd0647d79896') {
      return;
    }
    // this added code for heart rate take from puls ox filter(HeartRateFromPulsOx)
    var readings = undefined;
    var readingValue = undefined
    var checkThis = undefined
    var result = _.includes(value.abbrev, "BT_");
    var subType = value.name;
    var description = value.description;
    var reading = value.reading;
    var mViolation = [];
    var weightNotification = 0;
    var weightEvent = 0;
    var weightChange = 0;
    let getOnlyRelatedViolation = 0;
    let totalWeightReading = 0;
    if (result) {
      const matchOpts = {
        subType,
        description,
        reading,
        isBodytrace: true,
        readingsSource: props.readings,
        bodytraceReadings: props.bodytraceReading,
      };
      mViolation = filter(props.violations, (o) =>
        violationMatchesReading(o, matchOpts)
      );
      readings = _.filter(props.bodytraceReading, (reading) => {
        if (_.includes(value.abbrev, "WS") && reading.values.unit == 0) {
          return false;
        } else if (value.name.toLowerCase() in reading.values) {
          return true;
        }
        return false;
      });
      readingValue = _.map(readings, "values");
      checkThis = _.map(readingValue, (readingIndex, i) => {
        return _.get(readingValue[i], value.name.toLowerCase());
      });
      getOnlyRelatedViolation = filter(props.violations, (o) =>
        violationMatchesReading(o, matchOpts)
      );
    
    } else {
      const matchOpts = {
        subType,
        description,
        reading,
        isBodytrace: false,
        readingsSource: props.readings,
        bodytraceReadings: props.bodytraceReading,
      };
      mViolation = filter(props.violations, (o) =>
        violationMatchesReading(o, matchOpts)
      );
      readings = _.filter(props.readings, {
        readingType: value.reading
      });
      readingValue = _.map(readings, "value");
      checkThis = _.map(readingValue, (readingIndex) => {
        return readingIndex[value.index];
      });
      getOnlyRelatedViolation = filter(props.violations, (o) =>
        violationMatchesReading(o, matchOpts)
      );
    }
    if(description == 'Weight Difference' || subType == 'Weight'){
      _.filter(readings, (reading) => {
        const eventCount = _.filter(mViolation, (el) =>
          el.reading &&
          el.reading._id.toString() === reading._id.toString() &&
          isEventViolation(el)
        );
        const exceptionCount = _.filter(mViolation, (el) =>
          el.reading &&
          el.reading._id.toString() === reading._id.toString() &&
          !isEventViolation(el)
        );
        if(size(eventCount) == 0 && size(exceptionCount) == 0){
          weightChange = weightChange + 1;
        }
        if(size(eventCount) > 0){
          weightEvent = weightEvent + 1;
        }
        if(size(exceptionCount) > 0){
          weightNotification = weightNotification + 1;
        }
      });
      totalWeightReading = size(checkThis);
    }
    var checkNan = isNaN(_.min(checkThis));
    if (readings.length === 0 || checkNan || readings.length === 0 ) return;
    if (getOnlyRelatedViolation.length > 0 || checkThis.length > 0) {
      const smsViolationsSize = size(
        getOnlyRelatedViolation.filter(function (i) {
          return (
            (i.patientNotifications && i.patientNotifications.includes("sms")) ||
            (i.providerNotifications && i.providerNotifications.includes("sms")) ||
            (i.staffNotifications && i.staffNotifications.includes("sms"))
          );
        })
      );
      const inboxViolationsSize = size(
        getOnlyRelatedViolation.filter(function (i) {
          return (
            (i.patientNotifications && i.patientNotifications.includes("inbox")) ||
            (i.providerNotifications && i.providerNotifications.includes("inbox")) ||
            (i.staffNotifications && i.staffNotifications.includes("inbox"))
          );
        })
      );
      const emailViolationsSize = size(
        getOnlyRelatedViolation.filter(function (i) {
          return (
            (i.patientNotifications && i.patientNotifications.includes("email")) ||
            (i.providerNotifications && i.providerNotifications.includes("email")) ||
            (i.staffNotifications && i.staffNotifications.includes("email"))
          );
        })
      );
      const voiceViolationsSize = size(
        getOnlyRelatedViolation.filter(function (i) {
          return (
            (i.patientNotifications && i.patientNotifications.includes("voice")) ||
            (i.providerNotifications && i.providerNotifications.includes("voice")) ||
            (i.staffNotifications && i.staffNotifications.includes("voice"))
          );
        })
      );
      var matchingExpectationsBreached = _.filter(props.expectation,
        function (o) {
          return (
            o && o.readingSubType.toLowerCase() == _.replace(_.replace(value.name, "Pulse", "Heart Rate").toLowerCase(), "bpm", "Heart Rate").toLowerCase() &&
            !o.name.toLowerCase().includes("Event".toLowerCase()) 
          );
        }
      );
      var expectationList = matchingExpectationsBreached;
      // Checked all of the readings to see which had a low threshold.
      var lowExpectation = _.flatMap(_(expectationList)
        .filter((o) => o && o.condition == "<=" || o.condition == "between")
        .flatMap((_value, i) => _.pick(_value, "valueProperty"))
        .value(), "valueProperty");

      var highExpectation = _.flatMap(_(expectationList)
        .filter((o) => o && o.condition == ">=" || o.condition == "between")
        .flatMap((_value, i) => _.pick(_value, "valueProperty"))
        .value(), "valueProperty");

      var lowMinThreshold = _.maxBy(_.map(lowExpectation, i => i.minValue || i.value), function (o) { return o && parseInt(o) });
      var highMaxThreshold = _.minBy(_.map(highExpectation, i => i.maxValue || i.value), function (o) { return o && parseInt(o) });
      var lowThreshold = _.filter(checkThis, (reading, i) => {
        if(value.abbrev == 'TM'){
          reading = ((reading * 9) / 5 + 32).toFixed(2);
        }
        if (lowMinThreshold != undefined) {
          return lowMinThreshold != undefined ? parseFloat(reading) < parseFloat(lowMinThreshold) : parseFloat(reading) < parseFloat(lowMinThreshold);
        } else {
          return false;
        }
      });
      // Checked all of the readings to see which had a high threshold.
      var highThreshold = _.filter(checkThis, (reading, i) => {
        if(value.abbrev == 'TM'){
          reading = ((reading * 9) / 5 + 32).toFixed(2);
        }
        if (highMaxThreshold != undefined) {
          return highMaxThreshold != undefined ? parseFloat(reading) > parseFloat(highMaxThreshold) : parseFloat(reading) > parseFloat(highMaxThreshold);
        } else {
          return false;
        }
      });
      var lastSevenDays = _.size(checkThis);
      var countLowThreshold = Math.round(
        (_.size(lowThreshold) / lastSevenDays) * 100
      );
      var countHighThreshold = Math.round(
        (_.size(highThreshold) / lastSevenDays) * 100
      );
      var goodThreshold = Math.round(
        100 -
        (parseFloat(countLowThreshold) + parseFloat(countHighThreshold))
      );
      var goodReading = Math.round(
        lastSevenDays - (_.size(lowThreshold) + _.size(highThreshold))
      );
      var weightNotificationPer = Math.round(
          (weightNotification / lastSevenDays) * 100
      );
      var weightEventPer = Math.round(
          (weightEvent / lastSevenDays) * 100
      );
      var weightChangePer = Math.round(
          (weightChange / lastSevenDays) * 100
      );
      const trendingEventCount = _.size(
        _.filter(mViolation, (v) =>
          v.ruleType === "Trending Violation" ||
          (v.ruleType === "Vitals" &&
            v.name &&
            v.name.toLowerCase().includes("event"))
        )
      );
      const eventExpectationCount = _.size(
        _.filter(mViolation, (v) => v.ruleType === "Event Expectation")
      );
      const exceptionCount = _.size(
        _.filter(mViolation, (v) => getViolationCategory(v) === "Exception")
      );
      var symbolName = ["Systolic","Diastolic","Heart Rate","Weight"];
      var readingName = _.replace(value.name, "Pulse", "Heart Rate");
      return (
        <div className="my-5 break-page-reading-type">
          <hr style={{ borderTop: "3px solid #5c5959" }} />
          <h3 className="text-center font-weight-bold">            
            <div>
              {readingName} ({value.description})
            </div>
          </h3>
          <Row>
            <Col>
              <InterventionDetails 
              violations={mViolation}
              readings={readings} />
            </Col>
          </Row>
          <Row className="mt-1">
            <Col>
              <div className="scrollbar-table-cls" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th
                      colSpan="2"
                      className="text-black text-dark text-center mx-auto"
                    >
                      { symbolName.includes(value.name) ? (value.name != 'Weight' ? value.name + " Vital Range Results" : "Unhealthy Weight Change Results" ) : value.name.replace("Pulse","Heart Rate") + " "+ moment(props.start).format("MMMM") + " Out of Range Results" }
                    </th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  <tr>
                    <td>{(value.description != 'Weight Difference' && value.name != 'Weight') ? value.name + ' Low' : 'Unhealthy Weight Change Notification '} %</td>
                    <td>
                      {value.description != 'Weight Difference' && value.name != 'Weight' && (expectationList !== undefined && expectationList.length > 0) && (
                        <div> {countLowThreshold || 0}% {"(" + _.size(lowThreshold) + ")"}</div>
                      )}
                      {value.description != 'Weight Difference' && value.name != 'Weight' && (expectationList == undefined || expectationList.length == 0) && (
                        <div style={{color:"black"}}> Not included in Care Plan</div>
                      )}
                      {(value.description == 'Weight Difference' || value.name == 'Weight') && (
                         <div> {weightNotificationPer || 0}% {"(" + weightNotification + ")"}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>{(value.description != 'Weight Difference' && value.name != 'Weight') ? value.name + ' High' : 'Unhealthy Weight Change Event '} %</td>
                    <td>
                      {value.description != 'Weight Difference' && value.name != 'Weight' && (expectationList !== undefined && expectationList.length > 0) && (
                        <div> {countHighThreshold || 0}% {"(" + _.size(highThreshold) + ")"}</div>
                      )}
                      {value.description != 'Weight Difference' && value.name != 'Weight' && (expectationList == undefined || expectationList.length == 0) && (
                        <div style={{color:"black"}}> Not included in Care Plan</div>
                      )}
                      {(value.description == 'Weight Difference' || value.name == 'Weight') && (
                        <div>{weightEventPer || 0}% {"(" + weightEvent + ")"}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>{(value.description != 'Weight Difference' && value.name != 'Weight') ? value.name + ' Good' : 'No Unhealthy Weight Change '}  %</td>
                    <td>
                      {value.description != 'Weight Difference' && value.name != 'Weight' && (expectationList !== undefined && expectationList.length > 0) && (
                        <div> {goodThreshold || 0}% {"(" + goodReading + ")"}</div>
                      )}
                      {value.description != 'Weight Difference' && value.name != 'Weight' && (expectationList == undefined || expectationList.length == 0) && (
                        <div style={{color:"black"}}> Not included in Care Plan</div>
                      )}
                      {(value.description == 'Weight Difference' || value.name == 'Weight') && (
                        <div>{weightChangePer || 0}% {"(" + weightChange + ")"}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>{(value.description != 'Weight Difference' && value.name != 'Weight') ? 'Total Readings' : 'Total Weight Readings'}</td>
                    <td>{(value.description != 'Weight Difference' && value.name != 'Weight') ?  (checkThis ? _.size(checkThis) : 0) : totalWeightReading }</td>
                  </tr>
                  {value.description != 'Weight Difference' && value.name != 'Weight' && (
                    <>
                      <tr>
                        <td>Exceptions</td>
                        <td>{exceptionCount}</td>
                      </tr>
                      <tr>
                        <td>Trending Events</td>
                        <td>{trendingEventCount}</td>
                      </tr>
                      <tr>
                        <td>Event Expectations</td>
                        <td>{eventExpectationCount}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
              </div>
            </Col>
            <Col>
              <div className="scrollbar-table-cls" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
              <table className="table table-bordered scrollbar-table-cls" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <thead>
                  <tr>
                    <th
                      colSpan="2"
                      className="text-black text-dark text-center mx-auto"
                    >
                      {readingName} Intervention Totals
                    </th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  <tr>
                    <td>SMS Notification</td>
                    <td>{smsViolationsSize}</td>
                  </tr>
                  <tr>
                    <td>Inbox Notification</td>
                    <td>{inboxViolationsSize}</td>
                  </tr>
                  <tr>
                    <td>Email Notification</td>
                    <td>{emailViolationsSize}</td>
                  </tr>
                  <tr>
                    <td>Voice Notification</td>
                    <td>{voiceViolationsSize}</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </Col>
          </Row>
        </div>
      );
    } else {
      return <></>;
    }            
  });
}
export default ReadingSubTypeSummary;

function InterventionDetails(props) {
  return (
     <div className="scrollbar-table-cls" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
    <table className="table table-bordered attestation-print-table intervention-details-table">
      <colgroup>
        <col className="col-date" />
        <col className="col-type" />
        <col className="col-notifications" />
        <col className="col-name" />
        <col className="col-condition" />
        <col className="col-value" />
      </colgroup>
      <thead>
        <tr>
          <th className="text-black text-dark text-center mx-auto">
            Date
          </th>
          <th className="text-black text-dark text-center mx-auto">
            Type
          </th>
          <th className="text-black text-dark text-center mx-auto">
            Notifications Sent
          </th>
          <th className="text-black text-dark text-center mx-auto">Name</th>
          <th className="text-black text-dark text-center mx-auto">
            Condition
          </th>
          <th className="text-black text-dark text-center mx-auto">Value</th>
        </tr>
      </thead>    
      <tbody className="text-center">
        {props.violations.length > 0 ? (
          props.violations.map((violation, index) => {
            var value = 0;
            if(violation.reading && !violation.reading.imei){
              if(violation.reading && violation.reading.readingValue && violation.reading.readingValue.BPM){
                value = violation.reading && violation.reading.readingValue  ? violation.reading.readingValue.BPM : <div className="text-center"> - </div>;
              } else {
                value = violation.reading && violation.reading.readingValue && violation.ruleType != 'Weight'  ? (violation.readingSubType != 'Weight' ? (violation.readingSubType != "Body Temperature" ? violation.reading.readingValue[
                  violation.readingSubType.toLowerCase().replace("heart rate","pulseRate").replace("oxygen saturation","spo2").replace("body temperature","temperature").replace("bpm","BPM")
                ] : (Math.round(violation.reading.value * 9) / 5 + 32))  : (Math.round(violation.reading.value * 2.20462))) : (violation.ruleType == 'Weight' ? (Math.round(violation.reading.value * 2.20462)) : <div className="text-center"> - </div>);
              }
            } else {
              value = violation.reading && violation.reading.values  ? violation.reading.values[
                violation.readingSubType.toLowerCase().replace("heart rate","pulse").replace("oxygen saturation","spo2").replace("body temperature","temperature").replace("bpm","BPM").replace("ws","weight")
              ] : <div className="text-center"> - </div>;
            }
            const notifications = getViolationNotificationDisplay(violation);
            return (
              <tr key={violation._id || `${violation.createdAt}-${index}`}>
                <td className="text-center">{moment(violation.createdAt).format("MM/DD/YYYY")}</td>
                <td className="text-center align-middle">
                  <ViolationTypeCell violation={violation} />
                </td>
                <td className="text-left">
                  <p className="mb-1">
                    Patient:{" "}
                    {notifications.patient}
                  </p>
                  <p className="mb-1">
                    Provider:{" "}
                    {notifications.provider}
                  </p>
                  <p className="mb-0">
                    Staff:{" "}
                    {notifications.staff}
                  </p>
                </td>
                <td className="text-left">{violation.name}</td>
                <td className="text-center">{formatViolationCondition(violation)}</td>
                <td className="text-center">
                  {value}
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan="6" style={{ color: "red" }}>
              No Events or Exceptions
            </td>
          </tr>
        )}
      </tbody>
    </table>
    </div>
  );
}
